import React, { useEffect, useState, useCallback, useReducer } from "react";
import { useDispatch } from "react-redux";
import {
  useServiceMode,
  useAlarmsLimits,
  useSystemConfig,
  useSolutionType,
  useSystemId,
} from "../../../../hooks/useReduxSelectors";
import { AlarmLimits } from "../../../../actions/apiActions";
import EditAlarmLimitsRow from "./EditAlarmLimitsRow";
import { getAllAlarmLimits, updateReduxState, updateReduxStateAfterRestore } from "../../../../ducks/alarmsLimitsDuck";
import { Box, Button, Typography } from "@mui/material";


const alarmsLimitsReducer = (state, action) => {
  switch (action.type) {
    // happens on load
    case "form":
      return [
        ...action.payload,
      ];
    // happens when input field is changed
    case "CHANGE_FIELD":
      let alarm = state.filter((alarm) => alarm.frontendId === action.payload.frontendId); // to check if the alarm if found from state
      if (alarm) {
        let newAlarms = state.map((alarm, idx) => { // loop trough state
          let newAlarm = { ...alarm }; // copy the alarm from state
          if (alarm.frontendId === action.payload.frontendId) {
            newAlarm[action.payload.valueName] = action.payload.value; // update the correct alarm value
          }
          return newAlarm;
        });
        return state = newAlarms; // return updated state
      };
    // happens when user restores alarm to factory default values
    case "RESTORE_ALARM":
      let restoredLimits = action.payload.limits;
      let restoredAlarmFrontendId = restoredLimits.hwId + "|" + restoredLimits.valueName;
      let alarmToBeRestored = state.filter((alarmToBeRestored) => alarmToBeRestored.frontendId === restoredAlarmFrontendId); // to check if the alarm if found from state
      if (alarmToBeRestored) {
        let newAlarms = state.map((alarm, idx) => {
          let newAlarm = { ...alarm };
          if (alarm.frontendId === restoredAlarmFrontendId) {
            Object.keys(newAlarm).forEach(key => { // loop trough alarms' values..
              if (restoredLimits[key]) { // ... if restoredValues contain the same key as looped alarms' key e.g. "lowWarning" ...
                newAlarm[key] = restoredLimits[key]; // ... update its value
              }
            });
          }
          return newAlarm;
        });
        return state = newAlarms;
      }
    default:
      return state;
  }
};


const headerSpanSx = {
  display: "inline-block",
  flex: 1,
  minWidth: 0,
  alignSelf: "center",
  textAlign: "center",
  letterSpacing: "1px",
  fontSize: "18px",
};

const getAlarmDeactivatedState = (alarm) => {
  if (typeof alarm?.deactivated === "boolean") {
    return alarm.deactivated;
  }
  return false;
};


const EditRangesTable = ({ }) => {

  const systemId = useSystemId();
  const dispatch = useDispatch();
  const serviceModeActive = useServiceMode()?.isActive;
  const alarmsLimits = useAlarmsLimits();
  const alarmRangesList = alarmsLimits?.formattedEntries;
  const config = useSystemConfig();
  const solutionType = useSolutionType();
  let editAlarmDeviceHeaderTracker = null;

  const initialState = alarmRangesList ? alarmRangesList : [];

  const [formState, dispatchFormState] = useReducer(
    alarmsLimitsReducer,
    initialState,
  );

  const [alarmDeactivatedOverrides, setAlarmDeactivatedOverrides] = useState({});

  const saveForm = () => {
    let saveState = { ...formState };
    dispatch(updateReduxState({ systemId, saveState }));
  };

  const restoreValuesAndUpdateForm = async (params) => {
    let payload = {
      ...params,
      CustomerId: config.CUSTOMER_ID,
      DeviceId: config.DEVICE_ID
    };
    try {
      const response = await AlarmLimits.restore(config.DATA_URL, payload);
      dispatchFormState({
        type: "RESTORE_ALARM",
        payload: response
      });
      dispatch(updateReduxStateAfterRestore({ systemId: systemId, response, formState: formState }));
    } catch (e) {
      console.log(e);
    }
  };

  const onFetchAllAlarmLimits = useCallback(() => {
    dispatch(getAllAlarmLimits({ systemId, solutionType }));
  }, [systemId]);

  useEffect(() => {
    onFetchAllAlarmLimits();
  }, [systemId]);

  useEffect(() => {
    dispatchFormState({
      type: "form",
      payload: alarmRangesList || [],
    });

    // Keep optimistic override until backend data reflects the same state.
    setAlarmDeactivatedOverrides((prev) => {
      if (!prev || Object.keys(prev).length === 0) {
        return prev;
      }

      const next = { ...prev };
      const list = alarmRangesList || [];

      Object.keys(prev).forEach((frontendId) => {
        const alarm = list.find((entry) => entry.frontendId === frontendId);
        if (!alarm) {
          return;
        }

        if (getAlarmDeactivatedState(alarm) === prev[frontendId]) {
          delete next[frontendId];
        }
      });

      return next;
    });
  }, [alarmRangesList]);

  const onChangeHandler = useCallback((valueName, value, frontendId) => {
    dispatchFormState({
      type: "CHANGE_FIELD",
      payload: {
        valueName: valueName,
        value: value,
        frontendId: frontendId
      }
    });
  }, []);

  const isAlarmDeactivated = useCallback((alarm) => {
    if (Object.prototype.hasOwnProperty.call(alarmDeactivatedOverrides, alarm.frontendId)) {
      return alarmDeactivatedOverrides[alarm.frontendId];
    }

    return getAlarmDeactivatedState(alarm);
  }, [alarmDeactivatedOverrides]);

  const isDeviceDeactivated = useCallback((hwId) => {
    const deviceAlarms = (formState || []).filter((alarm) => alarm.hwId === hwId);
    if (deviceAlarms.length === 0) {
      return false;
    }

    return deviceAlarms.every((alarm) => isAlarmDeactivated(alarm));
  }, [formState, isAlarmDeactivated]);

  const toggleAllAlarmsForDevice = useCallback(async (hwId, currentDeactivatedState) => {
    if (!serviceModeActive) {
      return;
    }

    const payload = {
      HwId: hwId,
      Deactivated: !currentDeactivatedState,
      CustomerId: config.CUSTOMER_ID,
      DeviceId: config.DEVICE_ID,
    };

    try {
      await AlarmLimits.toggleAllForDevice(config.DATA_URL, payload);
      setAlarmDeactivatedOverrides((prev) => {
        const next = { ...prev };
        (formState || [])
          .filter((alarm) => alarm.hwId === hwId)
          .forEach((alarm) => {
            next[alarm.frontendId] = !currentDeactivatedState;
          });
        return next;
      });
      onFetchAllAlarmLimits();
    } catch (e) {
      console.log(e);
    }
  }, [config.CUSTOMER_ID, config.DATA_URL, config.DEVICE_ID, formState, onFetchAllAlarmLimits, serviceModeActive]);

  const toggleSingleAlarm = useCallback(async (alarm, currentDeactivatedState) => {
    if (!serviceModeActive) {
      return;
    }

    const payload = {
      HwId: alarm.hwId,
      AlarmValueName: alarm.valueName,
      Deactivated: !currentDeactivatedState,
      CustomerId: config.CUSTOMER_ID,
      DeviceId: config.DEVICE_ID,
    };

    try {
      await AlarmLimits.toggleSingle(config.DATA_URL, payload);
      setAlarmDeactivatedOverrides((prev) => ({
        ...prev,
        [alarm.frontendId]: !currentDeactivatedState,
      }));
      onFetchAllAlarmLimits();
    } catch (e) {
      console.log(e);
    }
  }, [config.CUSTOMER_ID, config.DATA_URL, config.DEVICE_ID, onFetchAllAlarmLimits, serviceModeActive]);

  return (
    <Box sx={{ height: "100%", overflow: "auto", borderRadius: "3px", m: "15px" }}>
      <Box sx={{ display: "flex", justifyContent: "stretch", bgcolor: "background.light", borderRadius: 1, mb: 0.25, py: 0.5 }}>
        <Box sx={{ ...headerSpanSx, textAlign: "left", pl: "30px" }}>Alarm ID</Box>
        <Box sx={headerSpanSx}>Warning Range</Box>
        <Box sx={headerSpanSx}>Alarm Range</Box>
        <Box sx={headerSpanSx} />
      </Box>
      <Box sx={{ mt: "5px" }}>
        {formState &&
          formState.map((alarm, idx) => {
            const displayHeader = alarm.name !== editAlarmDeviceHeaderTracker;
            const alarmDeactivated = isAlarmDeactivated(alarm);
            const deviceDeactivated = isDeviceDeactivated(alarm.hwId);
            editAlarmDeviceHeaderTracker = alarm.name; // Update the tracker for the next iteration.

            return (
              <div key={`${alarm.hwId}_${alarm.valueName}_loopIndex${idx}`}>
                {
                  displayHeader &&
                  <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap:2, marginBottom: 1, marginTop: 2 }}>
                    <Typography variant="h6">{alarm.name}</Typography>
                    {serviceModeActive && (
                      <Button variant="outlined" size="small" onClick={() => toggleAllAlarmsForDevice(alarm.hwId, deviceDeactivated)}>
                        {deviceDeactivated ? "Activate All Alarms" : "Disable All Alarms"}
                      </Button>
                    )}
                  </Box>
                }

                <EditAlarmLimitsRow
                  alarm={alarm}
                  isDeactivated={alarmDeactivated}
                  onToggleAlarmState={toggleSingleAlarm}
                  config={config}
                  serviceModeActive={serviceModeActive}
                  solutionType={solutionType}
                  onChangeHandler={onChangeHandler}
                  saveForm={saveForm}
                  restoreValuesAndUpdateForm={restoreValuesAndUpdateForm}
                />
              </div>
            );
          })}
        {(!formState || (formState && formState.length === 0)) && (
          <Box sx={{ display: "flex", justifyContent: "stretch", bgcolor: "background.light", borderRadius: 1, mb: 0.25, py: 0.5 }}>
            <Box component="span" sx={{ textAlign: "center", width: "100%", display: "block", flex: 1 }}>No Alarm Ranges</Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};



export default EditRangesTable;